import React, { useState, useEffect } from "react";
import { NotificationManager } from "react-notifications";
import { hideLoader, showLoader } from "../../utils/loader";
import { customBaseUrl, httpGet, httpPost } from "../../store/http";
import LoanDashboardLayout from '../../components/Layout/LoanDashboardLayout';
import { Button, Modal, Form, Input, Tabs, Table, Space, Row, Col, Select, DatePicker, Card, Descriptions, Spin } from "antd";
import { DownloadOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import AntTable from "../../components/CustomTable/AntTable";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import AdminLayout from "../../components/Layout";

const { TabPane } = Tabs;
const { Option } = Select;
const { RangePicker } = DatePicker;

const FinanceApproverDisbursement = ({ from }) => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [disbursedLoansData, setDisbursedLoansData] = useState([]);
    const [activeTab, setActiveTab] = useState("readyForDisbursement");
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [currentRecord, setCurrentRecord] = useState(null);
    const [rejectLoading, setRejectLoading] = useState(false);

    // Approve Disbursement Modal states
    const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
    const [approveLoading, setApproveLoading] = useState(false);
    const [repaymentSettings, setRepaymentSettings] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [recordToApprove, setRecordToApprove] = useState(null);

    // Filter states
    const [search, setSearch] = useState("");
    const [loanType, setLoanType] = useState(null);
    const [loanStatus, setLoanStatus] = useState(null);
    const [dateRange, setDateRange] = useState([]);

    const getLoans = async () => {
        showLoader();
        const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
        if (!token) {
            NotificationManager.error("Authentication token missing. Please log in.");
            hideLoader();
            return;
        }
        let queryParams = "";
        if (search) queryParams += `search=${search}&`;
        if (loanType) queryParams += `type=${loanType}&`;
        if (loanStatus) queryParams += `status=${loanStatus}&`;
        if (dateRange && dateRange.length === 2) {
            queryParams += `startDate=${dateRange[0].format("YYYY-MM-DD")}&`;
            queryParams += `endDate=${dateRange[1].format("YYYY-MM-DD")}&`;
        }
        if (queryParams) queryParams = `?${queryParams.slice(0, -1)}`;

        try {
            const res = await httpGet(
                `${customBaseUrl.loanUrl}/api/v1/loans/ready-for-disbursement${queryParams}`,
                null,
                false,
                false,
                token
            );

            if (res.status) {
                if (Array.isArray(res.data)) {
                    setData(res.data);
                } else {
                    console.error("FinanceApproverDisbursement: API response data is not an array:", res.data);
                    setData([]);
                    NotificationManager.error("Invalid data format received from API.");
                }
                hideLoader();
            } else {
                hideLoader();
                NotificationManager.error(res.message || "Failed to fetch loans.");
            }
        } catch (error) {
            console.error("FinanceApproverDisbursement: Error fetching loans:", error);
            NotificationManager.error("Error fetching loans for disbursement.");
            hideLoader();
        }
    }

    const getDisbursedLoans = async () => {
        showLoader();
        const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
        if (!token) {
            NotificationManager.error("Authentication token missing. Please log in.");
            hideLoader();
            return;
        }

        let queryParams = "";
        if (search) queryParams += `search=${search}&`;
        if (loanType) queryParams += `type=${loanType}&`;
        if (loanStatus) queryParams += `status=${loanStatus}&`;
        if (dateRange && dateRange.length === 2) {
            queryParams += `startDate=${dateRange[0].format("YYYY-MM-DD")}&`;
            queryParams += `endDate=${dateRange[1].format("YYYY-MM-DD")}&`;
        }
        if (queryParams) queryParams = `?${queryParams.slice(0, -1)}`;

        try {
            const res = await httpGet(
                `${customBaseUrl.loanUrl}/api/v1/loans/disbursed-with-payments${queryParams}`,
                null,
                false,
                false,
                token
            );

            if (res.status) {
                if (Array.isArray(res.data)) {
                    setDisbursedLoansData(res.data);
                } else {
                    console.error("FinanceApproverDisbursement: API response data for disbursed-with-payments is not an array:", res.data);
                    setDisbursedLoansData([]);
                    NotificationManager.error("Invalid data format received for disbursed loans.");
                }
                hideLoader();
            } else {
                hideLoader();
                NotificationManager.error(res.message || "Failed to fetch disbursed loans.");
            }
        } catch (error) {
            console.error("FinanceApproverDisbursement: Error fetching disbursed loans:", error);
            NotificationManager.error("Error fetching disbursed loans with payments.");
            hideLoader();
        }
    }

    const handleDownloadExcel = () => {
        try {
            const activeData = activeTab === "readyForDisbursement" ? data : disbursedLoansData;
            if (activeData.length === 0) {
                NotificationManager.warning("No data available to download.");
                return;
            }
            const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
            const fileExtension = ".xlsx";
            const tabName = activeTab === "readyForDisbursement" ? "Ready_For_Disbursement" : "Disbursed_Loans";
            const fileName = `${tabName}_${moment().format('YYYY-MM-DD_HH-mm')}`;

            const excelData = activeData.map(loan => ({
                'Loan ID': loan.loan_id,
                'First Name': loan.first_name,
                'Last Name': loan.last_name,
                'Email': loan.email,
                'Type': loan.type,
                'Loan Amount': loan.loan_amount,
                'Total Repayment': loan.total_repayment_amount,
                'Status': loan.status,
                'Start Date': loan.loan_start_date ? moment(loan.loan_start_date).format('YYYY-MM-DD') : 'N/A',
            }));

            const ws = XLSX.utils.json_to_sheet(excelData);
            const wb = { Sheets: { 'Loans': ws }, SheetNames: ['Loans'] };
            const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const dataFile = new Blob([excelBuffer], { type: fileType });
            FileSaver.saveAs(dataFile, fileName + fileExtension);
            NotificationManager.success('Excel download started');
        } catch (error) {
            console.error('Excel download error:', error);
            NotificationManager.error('Failed to download Excel');
        }
    };

    const handleResetFilters = () => {
        setSearch("");
        setLoanType(null);
        setLoanStatus(null);
        setDateRange([]);
        if (activeTab === "readyForDisbursement") {
            getLoans();
        } else {
            getDisbursedLoans();
        }
    }

    const handleSearch = () => {
        if (activeTab === "readyForDisbursement") {
            getLoans();
        } else {
            getDisbursedLoans();
        }
    }

    const handleApprove = (record) => {
        setRecordToApprove(record);
        setRepaymentSettings(null);
        setIsApproveModalVisible(true);
        fetchRepaymentSettings();
    };

    const fetchRepaymentSettings = async () => {
        setSettingsLoading(true);
        const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
        try {
            const res = await httpGet(
                `${customBaseUrl.loanUrl}/api/v1/admin/loan-settings`,
                null,
                false,
                false,
                token
            );
            if (res.status && res.data) {
                setRepaymentSettings(res.data);
            } else {
                NotificationManager.error(res.message || "Failed to fetch repayment settings.");
            }
        } catch (error) {
            console.error("Error fetching repayment settings:", error);
            NotificationManager.error("Error fetching repayment settings.");
        } finally {
            setSettingsLoading(false);
        }
    };

    const handleApproveSubmit = async () => {
        if (!recordToApprove) return;
        setApproveLoading(true);
        showLoader();
        const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
        try {
            const res = await httpPost(
                `${customBaseUrl.loanUrl}/api/v1/loans/${recordToApprove.loan_id}/disburse`,
                {},
                false,
                token
            );
            if (res.status) {
                NotificationManager.success("Loan disbursed successfully");
                setIsApproveModalVisible(false);
                setRecordToApprove(null);
                setRepaymentSettings(null);
                getLoans();
            } else {
                NotificationManager.error(res.message || "Failed to disburse loan.");
            }
        } catch (error) {
            console.error("Error disbursing loan:", error);
            NotificationManager.error("An error occurred during disbursement.");
        } finally {
            setApproveLoading(false);
            hideLoader();
        }
    };

    const handleApproveCancel = () => {
        setIsApproveModalVisible(false);
        setRecordToApprove(null);
        setRepaymentSettings(null);
    };

    const handleReject = (record) => {
        setCurrentRecord(record);
        setIsRejectModalVisible(true);
    };

    const handleRejectCancel = () => {
        setIsRejectModalVisible(false);
        setRejectionReason('');
        setCurrentRecord(null);
    };

    const handleRejectSubmit = async () => {
        if (!rejectionReason.trim()) {
            NotificationManager.error("Rejection reason is required.");
            return;
        }
        setRejectLoading(true);
        const token = from === 'admin' ? localStorage.getItem('token') : localStorage.getItem('loanAdminToken');
        try {
            const res = await httpPost(
                `${customBaseUrl.loanUrl}/api/v1/admin/actions/${currentRecord.loan_id}/reject-offer-letter`,
                { rejection_reason: rejectionReason },
                false,
                token
            );
            if (res.status) {
                NotificationManager.success("Offer letter rejected successfully");
                handleRejectCancel();
                getLoans();
            } else {
                NotificationManager.error(res.message || "Failed to reject offer letter.");
            }
        } catch (error) {
            NotificationManager.error("An error occurred while rejecting the offer letter.");
        } finally {
            setRejectLoading(false);
        }
    };

    const columns = [
        {
            title: "Loan ID",
            dataIndex: "loan_id",
            key: "loan_id",
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
        },
        {
            title: "Loan Request Amount",
            dataIndex: "loan_amount",
            key: "loan_amount",
        },
        {
            title: "Repayment Amount",
            dataIndex: "total_repayment_amount",
            key: "total_repayment_amount",
        },
        {
            title: "First Name",
            dataIndex: "first_name",
            key: "first_name",
        },
        {
            title: "Last Name",
            dataIndex: "last_name",
            key: "last_name",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
        },
        {
            title: "Loan Start Date",
            dataIndex: "loan_start_date",
            key: "loan_start_date",
            render: (text) => text ? moment(text).format('YYYY-MM-DD') : 'N/A',
        },
        {
            title: "Offer Letter",
            key: "offer_letter",
            render: (text, record) => (
                record.offer_url ? (
                    <Button type="link" onClick={() => window.open(record.offer_url, '_blank')}>
                        View signed offer
                    </Button>
                ) : (
                    <span>N/A</span>
                )
            ),
        },
        {
            title: "Action",
            key: "action",
            render: (text, record) => {
                if (activeTab === "readyForDisbursement") {
                    const loanStartDateMoment = record.loan_start_date ? moment(record.loan_start_date) : null;
                    const isBeforeStartDate = loanStartDateMoment ? moment().isBefore(loanStartDateMoment, 'day') : true;

                    return (
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => handleApprove(record)}
                                // disabled={isBeforeStartDate}
                                title={isBeforeStartDate ? `Approval active from ${loanStartDateMoment.format('YYYY-MM-DD')}` : ''}
                            >
                                Approve Disbursement
                            </Button>
                            <Button
                                danger
                                onClick={() => handleReject(record)}
                            >
                                Reject Offer
                            </Button>
                        </Space>
                    );
                } else if (activeTab === "disbursedWithPayments") {
                    return (
                        <Button
                            type="primary"
                            onClick={() => navigate(from === "admin" ? `/admin/loan/summary/${record.loan_id}` : `/admin/summary/${record.loan_id}`)}
                        >
                            View More
                        </Button>
                    );
                }
                return null;
            },
        },
    ];

    useEffect(() => {
        // const roles = from === 'admin' ? localStorage.getItem('roles') : JSON.parse(localStorage.getItem('roles'))
        // if (roles?.includes('finance_approver') || (roles?.includes('ROLE_OWNER')) || roles?.includes("SUPER_ADMIN")) {
        if (activeTab === "readyForDisbursement") {
            getLoans();
        } else if (activeTab === "disbursedWithPayments") {
            getDisbursedLoans();
        }
        // } else {
        //     // navigate(-1)
        // }
    }, [activeTab])
    const Layout = from === "admin" ? AdminLayout : LoanDashboardLayout;

    return (
        <Layout>
            <div>
                <h1>Finance Approver Disbursement</h1>
                <Tabs defaultActiveKey="readyForDisbursement" activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane tab="Ready for Disbursement" key="readyForDisbursement">
                        <Card title="Filter Pending Disbursements" style={{ marginBottom: 20 }}>
                            <Row gutter={[16, 16]} align="bottom">
                                <Col xs={24} sm={12} md={8}>
                                    <p style={{ marginBottom: 8 }}>Search (Name, Email, ID)</p>
                                    <Input
                                        placeholder="Search..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onPressEnter={handleSearch}
                                        prefix={<SearchOutlined />}
                                    />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <p style={{ marginBottom: 8 }}>Loan Type</p>
                                    <Select
                                        placeholder="Select Type"
                                        style={{ width: '100%' }}
                                        value={loanType}
                                        onChange={(value) => setLoanType(value)}
                                        allowClear
                                    >
                                        <Option value="Personal">Personal</Option>
                                        <Option value="Business">Business</Option>
                                        <Option value="POF">POF</Option>
                                    </Select>
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    <p style={{ marginBottom: 8 }}>Date Range</p>
                                    <RangePicker
                                        style={{ width: '100%' }}
                                        value={dateRange}
                                        onChange={(dates) => setDateRange(dates)}
                                    />
                                </Col>
                                <Col xs={12} md={2}>
                                    <Button type="primary" onClick={handleSearch} block icon={<SearchOutlined />}>
                                        Search
                                    </Button>
                                </Col>
                                <Col xs={12} md={2}>
                                    <Button onClick={handleResetFilters} block icon={<ReloadOutlined />}>
                                        Reset
                                    </Button>
                                </Col>
                            </Row>
                            <Row style={{ marginTop: 16 }}>
                                <Col span={24}>
                                    <Button
                                        type="dashed"
                                        onClick={handleDownloadExcel}
                                        icon={<DownloadOutlined />}
                                        disabled={data.length === 0}
                                    >
                                        Download as Excel
                                    </Button>
                                </Col>
                            </Row>
                        </Card>
                        <Table columns={columns} dataSource={data} rowKey="loan_id" scroll={{ x: 'max-content' }} />
                    </TabPane>
                    <TabPane tab="Disbursed Loans with Payments" key="disbursedWithPayments">
                        <Card title="Filter Disbursed Loans" style={{ marginBottom: 20 }}>
                            <Row gutter={[16, 16]} align="bottom">
                                <Col xs={24} sm={12} md={6}>
                                    <p style={{ marginBottom: 8 }}>Search (Name, Email, ID)</p>
                                    <Input
                                        placeholder="Search..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onPressEnter={handleSearch}
                                        prefix={<SearchOutlined />}
                                    />
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <p style={{ marginBottom: 8 }}>Loan Type</p>
                                    <Select
                                        placeholder="Select Type"
                                        style={{ width: '100%' }}
                                        value={loanType}
                                        onChange={(value) => setLoanType(value)}
                                        allowClear
                                    >
                                        <Option value="Personal">Personal</Option>
                                        <Option value="Business">Business</Option>
                                        <Option value="POF">POF</Option>
                                    </Select>
                                </Col>
                                <Col xs={24} sm={12} md={4}>
                                    <p style={{ marginBottom: 8 }}>Status</p>
                                    <Select
                                        placeholder="Select Status"
                                        style={{ width: '100%' }}
                                        value={loanStatus}
                                        onChange={(value) => setLoanStatus(value)}
                                        allowClear
                                    >
                                        <Option value="Disbursed">Disbursed</Option>
                                        <Option value="Completed">Completed</Option>
                                    </Select>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <p style={{ marginBottom: 8 }}>Date Range (Start Date)</p>
                                    <RangePicker
                                        style={{ width: '100%' }}
                                        value={dateRange}
                                        onChange={(dates) => setDateRange(dates)}
                                    />
                                </Col>
                                <Col xs={12} md={2}>
                                    <Button type="primary" onClick={handleSearch} block icon={<SearchOutlined />}>
                                        Search
                                    </Button>
                                </Col>
                                <Col xs={12} md={2}>
                                    <Button onClick={handleResetFilters} block icon={<ReloadOutlined />}>
                                        Reset
                                    </Button>
                                </Col>
                            </Row>
                            <Row style={{ marginTop: 16 }}>
                                <Col span={24}>
                                    <Button
                                        type="dashed"
                                        onClick={handleDownloadExcel}
                                        icon={<DownloadOutlined />}
                                        disabled={disbursedLoansData.length === 0}
                                    >
                                        Download as Excel
                                    </Button>
                                </Col>
                            </Row>
                        </Card>
                        <Table columns={columns} dataSource={disbursedLoansData} rowKey="loan_id" scroll={{ x: 'max-content' }} />
                    </TabPane>
                </Tabs>
            </div>
            <Modal
                title="Reject Offer Letter"
                visible={isRejectModalVisible}
                onOk={handleRejectSubmit}
                onCancel={handleRejectCancel}
                confirmLoading={rejectLoading}
                okText="Submit"
                cancelText="Cancel"
            >
                <Form layout="vertical">
                    <Form.Item label="Rejection Reason" required>
                        <Input.TextArea
                            rows={4}
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            placeholder="Please provide a reason for rejection"
                        />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title="Confirm Loan Disbursement"
                visible={isApproveModalVisible}
                onOk={handleApproveSubmit}
                onCancel={handleApproveCancel}
                confirmLoading={approveLoading}
                okText="Confirm & Disburse"
                cancelText="Cancel"
                width={600}
                destroyOnClose
            >
                {recordToApprove && (
                    <div>
                        <div style={{ marginBottom: 20, borderBottom: '1px solid #f0f0f0', paddingBottom: 15 }}>
                            <h4 style={{ fontWeight: 600, color: '#1f1f1f', marginBottom: 12 }}>Loan Details</h4>
                            <Descriptions column={2} size="small" bordered>
                                <Descriptions.Item label="Loan ID">{recordToApprove.loan_id}</Descriptions.Item>
                                <Descriptions.Item label="Type">{recordToApprove.type}</Descriptions.Item>
                                <Descriptions.Item label="Client Name">{`${recordToApprove.first_name} ${recordToApprove.last_name}`}</Descriptions.Item>
                                <Descriptions.Item label="Email">{recordToApprove.email}</Descriptions.Item>
                                <Descriptions.Item label="Requested Amount">{recordToApprove.loan_amount}</Descriptions.Item>
                                <Descriptions.Item label="Repayment Amount">{recordToApprove.total_repayment_amount}</Descriptions.Item>
                            </Descriptions>
                        </div>
                        <div>
                            <h4 style={{ fontWeight: 600, color: '#1f1f1f', marginBottom: 12 }}>Repayment Settings</h4>
                            {settingsLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                                    <Spin tip="Fetching repayment settings..." />
                                </div>
                            ) : repaymentSettings ? (
                                <Descriptions column={1} size="small" bordered>
                                    <Descriptions.Item label="First Repayment Delay">
                                        <span style={{ fontWeight: 600, color: '#ff6700' }}>
                                            {repaymentSettings.first_repayment_delay_value} {repaymentSettings.first_repayment_delay_unit}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Repayment Grace Period">
                                        <span style={{ fontWeight: 600, color: '#ff6700' }}>
                                            {repaymentSettings.repayment_grace_period_value} {repaymentSettings.repayment_grace_period_unit}
                                        </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Repayment Cycle">
                                        <span style={{ fontWeight: 600, color: '#ff6700' }}>
                                            {repaymentSettings.repayment_cycle_value} {repaymentSettings.repayment_cycle_unit}
                                        </span>
                                    </Descriptions.Item>
                                </Descriptions>
                            ) : (
                                <p style={{ color: '#ff4d4f' }}>Failed to load repayment settings.</p>
                            )}
                        </div>
                        <p style={{ marginTop: 20, color: '#8c8c8c', fontStyle: 'italic' }}>
                            Please verify the loan details and active repayment settings above before confirming disbursement.
                        </p>
                    </div>
                )}
            </Modal>
        </Layout>
    )
}

export default FinanceApproverDisbursement;
